import { useEffect, useMemo, useState } from 'react';
import { ListGroup } from 'react-bootstrap';
import Fuse from 'fuse.js';
import type { StockVehicle } from 'src/motornet/types';
import { useConfiguratorState } from '../store';
import { isStockRestorePending } from '../savedConfigurations/stockRestore';
import { formatCurrency, stripCode, titleCase } from '../common';
import { useConfiguratorTranslations } from '../translations';
import { useFuelFilter } from './useFuelFilter';
import { FuelChipRow } from './FuelChipRow';
import styles from './StockSelector.module.scss';
import shared from '../configurator.module.scss';

const STOCK_COLLAPSED_LIMIT = 10;

let autoSelectedStockFor: string | null = null;

/** @see resetAutoSelectedTrim — stessa disciplina, chiamata dagli stessi punti. */
export function resetAutoSelectedStock(): void {
	autoSelectedStockFor = null;
}

export function useAutoSelectFirstStock(vehicles: StockVehicle[]) {
	const makeCode = useConfiguratorState((s) => s.makeCode);
	const modelCode = useConfiguratorState((s) => s.modelCode);
	const stockVehicle = useConfiguratorState((s) => s.stockVehicle);
	const selectStockVehicle = useConfiguratorState((s) => s.selectStockVehicle);

	useEffect(() => {
		if (!vehicles.length) return;
		if (isStockRestorePending()) return;
		const key = `${makeCode ?? ''} ${modelCode ?? ''}`;
		if (autoSelectedStockFor === key) return;
		autoSelectedStockFor = key;
		if (stockVehicle) return;

		selectStockVehicle(vehicles[0]);
	}, [vehicles, makeCode, modelCode, stockVehicle, selectStockVehicle]);
}

function useStockSearch(vehicles: StockVehicle[], query: string) {
	const fuse = useMemo(
		() =>
			new Fuse(vehicles, {
				keys: ['make', 'model', 'trim', 'colors.exteriorLabel'],
				threshold: 0.35,
				includeScore: false,
			}),
		[vehicles]
	);

	return query.trim() ? fuse.search(query).map((r) => r.item) : vehicles;
}

function StockRow({ vehicle }: { vehicle: StockVehicle }) {
	const selected = useConfiguratorState((s) => s.stockVehicle);
	const selectStockVehicle = useConfiguratorState((s) => s.selectStockVehicle);
	const clearStockVehicle = useConfiguratorState((s) => s.clearStockVehicle);
	const tr = useConfiguratorTranslations();

	const isSelected = selected?.id === vehicle.id;

	const colour = vehicle.colors.exteriorLabel
		? stripCode(vehicle.colors.exteriorLabel)
		: '';
	const detail = [
		colour,
		vehicle.optionals.length > 0
			? tr.stockOptionalsCount(vehicle.optionals.length)
			: '',
	]
		.filter(Boolean)
		.join(' · ');

	return (
		<ListGroup.Item
			action
			active={isSelected}
			onClick={() =>
				isSelected ? clearStockVehicle() : selectStockVehicle(vehicle)
			}
			className={`d-flex align-items-center gap-3 ${styles.stockRow}`}
		>
			<span className="flex-grow-1">
				<span className={styles.stockName}>
					{vehicle.make} {vehicle.model} {vehicle.trim}
				</span>
				{detail && <div className={styles.stockDetail}>{detail}</div>}
			</span>
			<span className={styles.stockPrice}>
				{formatCurrency(vehicle.price.totalNoVat)}
			</span>
		</ListGroup.Item>
	);
}

export function StockList({
	vehicles,
	total,
	search,
}: {
	vehicles: StockVehicle[];
	total: number;
	search: string;
}) {
	const [expanded, setExpanded] = useState(false);
	const { chips, activeChip, setActiveChip, filtered } = useFuelFilter(
		vehicles,
		(v) => titleCase(v.specs.alimentazione)
	);
	const searched = useStockSearch(filtered, search);
	useAutoSelectFirstStock(searched);
	const visible = expanded
		? searched
		: searched.slice(0, STOCK_COLLAPSED_LIMIT);
	const tr = useConfiguratorTranslations();

	const truncated = total > vehicles.length;

	return (
		<div>
			<FuelChipRow
				chips={chips}
				activeChip={activeChip}
				onSelect={(chip) => {
					setActiveChip(chip);
					setExpanded(false);
				}}
			/>

			<div className={shared.filterGroupLabel}>{tr.stockSectionLabel}</div>
			{truncated && (
				<div className={styles.truncatedNote}>
					{tr.stockTruncated(vehicles.length, total)}
				</div>
			)}
			{searched.length === 0 ? (
				<div className={styles.emptyMsg}>{tr.stockEmpty}</div>
			) : (
				<>
					<ListGroup>
						{visible.map((vehicle) => (
							<StockRow key={vehicle.id} vehicle={vehicle} />
						))}
					</ListGroup>
					{!expanded && searched.length > STOCK_COLLAPSED_LIMIT && (
						<ListGroup className="mt-3">
							<ListGroup.Item
								action
								onClick={() => setExpanded(true)}
								className="text-center text-muted fst-italic py-3"
							>
								{tr.stockShowAll(searched.length)}
							</ListGroup.Item>
						</ListGroup>
					)}
				</>
			)}
		</div>
	);
}
